import { DateHelpers } from "../../helpers/date-helpers";
import { FormatHelpers } from "../../helpers/format-helpers";
import { ObjectHelpers } from "../../helpers/object-helpers";

export class SingleStockWindowItem {
    public id = 0;
    public name = '';
    public shortName = '';
    public value: number | null = null;
    public displayValue: string = '';
    public cssClass = '';

    constructor(data?: any) {
        if (data) {
            this.cast(data);
        }
    }

    private cast(data: any) {
        ObjectHelpers.setProperties(data, this, true);
    }
}

export class SingleStockWindowDataModel {
    public start: Date | null = null;
    public end: Date | null = null;
    public earningsDateUnixtime: number | null = null;
    public totalVolume: number | null = null;
    public relativeVolume: number | null = null;
    public float: number | null = null;
    public shortFloat: number | null = null;
    public todayRange: number | null = null;
    public daysToCover: number | null = null;
    public volumeToday: number | null = null;
    public changesFromCloseDollar: number | null = null;
    public changesFromClosePercentage: number | null = null;
    public marketCap: number | null = null;
    public revenue: number | null = null;
    public earningsPerShare: number | null = null;
    public sharesOutstanding: number | null = null;
    public cash: number | null = null;
    public optionsVolumeToday: number | null = null;
    public changeSinceJanuary: number | null = null;
    public dollarVolume: number | null = null;
    public currentDebt: number | null = null;
    public positionInRange: number | null = null;
    public positionIn5DayRange: number | null = null;
    public positionInYearRange: number | null = null;
    public optionsVolume: number | null = null;
    public callVolumeToday: number | null = null;
    public putVolumeToday: number | null = null;
    public changeLastMonth: number | null = null;
    public changeIn20Days: number | null = null;
    public changeLastWeek: number | null = null;
    public changeIn5Days: number | null = null;
    public quarterlyEarningsGrowth: number | null = null;
    public sma200: number | null = null;
    public dailyRSI: number | null = null;
    public volume1Minute: number | null = null;
    public volatility: number | null = null;
    public companyName: string | null = null;
    public symbol = '';

    constructor(data?: any) {
        if (data) {
            this.cast(data);
        }
    }

    public getSingleStockItems(includeTVAndRV: boolean, totalVolumeDisplayTopRight: boolean): Array<SingleStockWindowItem> {
        let result: Array<SingleStockWindowItem> = [];
        if (!this.symbol) {
            return [];
        }

        result = [
            new SingleStockWindowItem({
                id: 9,
                name: `Revenue:`,
                shortName: `Rev:`,
                value: this.revenue,
                displayValue: '$' + FormatHelpers.numberToString(this.revenue)
            }),
            new SingleStockWindowItem({
                id: 11,
                name: `Cash:`,
                shortName: `Cash:`,
                value: this.cash,
                displayValue: FormatHelpers.numberToString(this.cash),
            }),
            new SingleStockWindowItem({
                id: 15,
                name: `Current Debt:`,
                shortName: `Current Dbt:`,
                value: this.currentDebt,
                displayValue: '$' + FormatHelpers.numberToString(this.currentDebt),
            }),
            new SingleStockWindowItem({
                id: 8,
                name: `Earnings Per Share:`,
                shortName: `Earn Per Shr:`,
                value: this.earningsPerShare,
                displayValue: '$' + FormatHelpers.numberToString(this.earningsPerShare)
            }),
            new SingleStockWindowItem({
                id: 2,
                name: `Float:`,
                shortName: `Flt:`,
                value: this.float,
                displayValue: FormatHelpers.numberToString(this.float)
            }),
            new SingleStockWindowItem({
                id: 1,
                name: `Short Float %:`,
                shortName: `Shrt Flt %:`,
                value: this.shortFloat,
                displayValue: FormatHelpers.numberToString(this.shortFloat) + '%'
            }),
            new SingleStockWindowItem({
                id: 4,
                name: `Days to Cover:`,
                shortName: `Dys to Cvr:`,
                value: this.daysToCover,
                displayValue: FormatHelpers.numberToString(this.daysToCover)
            }),
            new SingleStockWindowItem({
                id: 10,
                name: `Shares Outstanding:`,
                shortName: `Shr Out:`,
                value: this.sharesOutstanding,
                displayValue: FormatHelpers.numberToString(this.sharesOutstanding),
            }),
            new SingleStockWindowItem({
                id: 5,
                name: `Earnings Date:`,
                shortName: `Earn Date:`,
                value: this.earningsDateUnixtime,
                displayValue: this.getEarningDiv()
            }),
            new SingleStockWindowItem({
                id: 6,
                name: `Market Cap:`,
                shortName: `Mkt Cap:`,
                value: this.marketCap,
                displayValue: '$' + FormatHelpers.numberToString(this.marketCap)
            }),
            new SingleStockWindowItem({
                id: 14,
                name: `Dollar Volume:`,
                shortName: `Dollar Volume:`,
                value: this.dollarVolume,
                displayValue: FormatHelpers.numberToString(this.dollarVolume),
            }),
            new SingleStockWindowItem({
                id: 12,
                name: `Options Volume Today %:`,
                shortName: `Opts Vol Today %:`,
                value: this.optionsVolumeToday,
                displayValue: FormatHelpers.numberToString(this.optionsVolumeToday) + '%',
            }),
            new SingleStockWindowItem({
                id: 16,
                name: `Position in Range:`,
                shortName: `Pos Rng:`,
                value: this.positionInRange,
                displayValue: FormatHelpers.numberToString(this.positionInRange),
            }),
            new SingleStockWindowItem({
                id: 17,
                name: `Position in 5 Day Range:`,
                shortName: `Pos 5 Day Rng:`,
                value: this.positionIn5DayRange,
                displayValue: FormatHelpers.numberToString(this.positionIn5DayRange),
            }),
            new SingleStockWindowItem({
                id: 18,
                name: `Position in Year Range:`,
                shortName: `Pos Yr Rng:`,
                value: this.positionInYearRange,
                displayValue: FormatHelpers.numberToString(this.positionInYearRange),
            }),
            new SingleStockWindowItem({
                id: 13,
                name: `Change Since January 1:`,
                shortName: `Chg Jan 1:`,
                value: this.changeSinceJanuary,
                displayValue: FormatHelpers.numberToString(this.changeSinceJanuary) + '%',
            }),
            new SingleStockWindowItem({
                id: 23,
                name: `Change in 20 Days %:`,
                shortName: `Chg in 20 Dys %:`,
                value: this.changeIn20Days,
                displayValue: FormatHelpers.numberToString(this.changeIn20Days) + '%',
            }),
            new SingleStockWindowItem({
                id: 25,
                name: `Change in 5 Days %:`,
                shortName: `Chg in 5 Dys %:`,
                value: this.changeIn5Days,
                displayValue: FormatHelpers.numberToString(this.changeIn5Days) + '%',
            }),
            new SingleStockWindowItem({
                id: 26,
                name: `Quarterly Earnings Growth:`,
                shortName: `Qtrly Earn Grwth:`,
                value: this.quarterlyEarningsGrowth,
                displayValue: FormatHelpers.numberToString(this.quarterlyEarningsGrowth),
            }),
            new SingleStockWindowItem({
                id: 20,
                name: `Call Volume Today:`,
                shortName: `Call Vol Today:`,
                value: this.callVolumeToday,
                displayValue: FormatHelpers.numberToString(this.callVolumeToday),
            }),
            new SingleStockWindowItem({
                id: 21,
                name: `Put Volume Today:`,
                shortName: `Put Vol Today:`,
                value: this.putVolumeToday,
                displayValue: FormatHelpers.numberToString(this.putVolumeToday),
            }),
            new SingleStockWindowItem({
                id: 7,
                name: `Volume Today %:`,
                shortName: `Vol Today %:`,
                value: this.volumeToday,
                displayValue: FormatHelpers.numberToString(this.volumeToday) + '%'
            }),
            new SingleStockWindowItem({
                id: 19,
                name: `Options Volume:`,
                shortName: `Opts Vol:`,
                value: this.optionsVolume,
                displayValue: FormatHelpers.numberToString(this.optionsVolume) + '%',
            }),
            new SingleStockWindowItem({
                id: 22,
                name: `Change Last Month:`,
                shortName: `Chg Lst Month:`,
                value: this.changeLastMonth,
                displayValue: '$' + FormatHelpers.numberToString(this.changeLastMonth),
            }),
            new SingleStockWindowItem({
                id: 24,
                name: `Change Last Week:`,
                shortName: `Chg Lst Week:`,
                value: this.changeLastWeek,
                displayValue: '$' + FormatHelpers.numberToString(this.changeLastWeek),
            }),
            new SingleStockWindowItem({
                id: 27,
                name: `200 Day SMA:`,
                shortName: `200 Day SMA:`,
                value: this.sma200,
                displayValue: FormatHelpers.numberToString(this.sma200),
            }),
            new SingleStockWindowItem({
                id: 28,
                name: `Daily RSI:`,
                shortName: `Daily RSI:`,
                value: this.dailyRSI,
                displayValue: FormatHelpers.numberToString(this.dailyRSI),
            }),
            new SingleStockWindowItem({
                id: 29,
                name: `Volume 1 Minute:`,
                shortName: `Vol 1 Min:`,
                value: this.volume1Minute,
                displayValue: FormatHelpers.numberToString(this.volume1Minute),
            }),
            new SingleStockWindowItem({
                id: 30,
                name: `Volatility:`,
                shortName: `Volatility:`,
                value: this.volatility,
                displayValue: '$' + FormatHelpers.numberToString(this.volatility),
            }),
            new SingleStockWindowItem({
                id: 3,
                name: `Today's Range %:`,
                shortName: `Today's Rng %:`,
                value: this.todayRange,
                displayValue: FormatHelpers.numberToString(this.todayRange) + '%'
            }),
        ];

        if (includeTVAndRV) {
            result.unshift(new SingleStockWindowItem({
                id: 32,
                name: `Total Volume:`,
                shortName: `Total Volume:`,
                value: this.totalVolume,
                displayValue: FormatHelpers.numberToString(this.totalVolume)
            }));
            result.unshift(new SingleStockWindowItem({
                id: 31,
                name: `Relative Volume:`,
                shortName: `Relative Volume:`,
                cssClass: (this.relativeVolume > 3 ? 'highlight2' : (this.relativeVolume > 1 ? 'highlight' : '')),
                value: this.relativeVolume,
                displayValue: FormatHelpers.numberToString(this.relativeVolume)
            }));
        } else if (!totalVolumeDisplayTopRight) {
            result.unshift(new SingleStockWindowItem({
                id: 32,
                name: `Total Volume:`,
                shortName: `Total Volume:`,
                value: this.totalVolume,
                displayValue: FormatHelpers.numberToString(this.totalVolume)
            }));
        }

        return result;
    }

    private getEarningDiv(): string {
        if (SingleStockWindowDataModel.doesNotHaveValue(this.earningsDateUnixtime)) {
            return '';
        }

        let foreColor = "black";
        let backColor = "white";
        const valueAsDouble = this.earningsDateUnixtime;
        if (-0.25 <= valueAsDouble && valueAsDouble < 2.0) {
            backColor = "red";
            foreColor = "antiqueWhite";
        } else if (2.0 <= valueAsDouble && valueAsDouble < 4.0) {
            backColor = "yellow";
            foreColor = "black";
        }

        const result = `
        <div class="earning-box" style="color: ${foreColor};background-color: ${backColor};">
            ${this.getEarningsDateText()}
        </div>`;

        return result;
    }

    public static hasValue(value: any): boolean {
        return !this.doesNotHaveValue(value);
    }

    public static doesNotHaveValue(value: any): boolean {
        if (value == null || isNaN(value)) {
            return true;
        }

        return false;
    }

    public static doesNotHaveValueOrZero(value: any): boolean {
        if (value == null || isNaN(value) || value == 0) {
            return true;
        }

        return false;
    }

    public getEarningDate(): Date | null {
        let currentDataDate: Date | null = null;
        if (this.end) {
            currentDataDate = new Date(this.end.valueOf());
        } else if (this.start) {
            currentDataDate = new Date(this.start.valueOf());
        }

        if (!currentDataDate || this.earningsDateUnixtime == null) {
            return null;
        }

        const businessDays = Math.trunc(this.earningsDateUnixtime);
        let targetDate: Date | null = null;
        if (this.earningsDateUnixtime < 0)
            targetDate = DateHelpers.addBusinessDay(currentDataDate, businessDays - 1);
        else
            targetDate = DateHelpers.addBusinessDay(currentDataDate, businessDays);


        return targetDate;
    }

    // Get the earnings date as text.
    public getEarningsDateText(): string {
        let retVal = "";
        const earningDate = this.getEarningDate();
        if (!earningDate) {
            return retVal;
        }

        const localeLanguage = `en-US`;
        const value = this.earningsDateUnixtime;
        if (value != null) {
            const options: any = { month: 'short', day: 'numeric' };
            const targetDateString = earningDate.toLocaleDateString(localeLanguage, options).toUpperCase();

            //now get the truncated portion of the business days (determine pre /post/current market hours
            const multiplier = 100; //the values that we have are either .25,.50,.75,-.25,-.50,-.75 (or 0) (two places past decimal
            let result = Math.round((value - Math.round(value)) * multiplier);
            let market = "";
            switch (result) {
                case 25:
                    market = "PRE-MKT";
                    break;
                case 50:
                    market = "MKT-HRS";
                    break;
                case 75:
                    market = "POST-MKT";
                    break;
                case -25:
                    market = "POST-MKT";
                    break;
                case -50:
                    market = "MKT-HRS";
                    break;
                case -75:
                    market = "PRE-MKT";
                    break;
                default:
                    market = "MKT-HRS";
                    break;
            }
            retVal = targetDateString + "<BR>" + market;
        }

        return retVal;
    }

    private cast(data: any) {
        ObjectHelpers.setProperties(data, this, true);
    }
}